/**
 * Composable for handling analytics identity integration across multiple services
 * This centralizes user identification logic for analytics services like Heap, Braze, etc. using a single, unified customer ID as the 'stitching ID'
 *
 * @remarks
 * - When adding additional integrations to link Customer ID to, follow the `linkTo` method approach inside the composable.
 * - Types for heap are defined in integrationScripts.ts
 * - Types for $braze are defined in the Braze plugin
 */

interface AnalyticsIdentityComposable {
  /**
   * Links customer ID to analytics platforms
   * @returns Cleanup function to stop watching
   */
  linkCustomerId: () => (() => void) | void
}

const noop = () => {}

/**
 * Composable for analytics identity management
 * @returns Analytics identity utility functions
 * @remarks Only works in client-side context
 */
export const useAnalyticsIdentity = (): AnalyticsIdentityComposable => {
  if (import.meta.server) {
    return {
      linkCustomerId: noop,
    }
  }
  const userStore = useUserStore()

  /**
   * Link user to Braze analytics platform
   * @param customerId - Salesforce customer ID
   */
  const linkToBraze = (customerId: string): void => {
    try {
      const { $braze } = useNuxtApp()
      // TODO: This watcher re-runs when userStore changes, but it does not wait for Braze identity
      // association. useLogin/useBrazeIdentity currently call changeUser() fire-and-forget, so this
      // can still run while Braze is on the anonymous profile and attach the alias there. Coordinate
      // aliasing with ensureIdentity() completion so salesforce_customer_id lands on the identified
      // Braze user. Out of scope for the banner fix; track separately.
      if ($braze?.sdk?.getUser) {
        $braze.sdk.getUser()?.addAlias(customerId, 'salesforce_customer_id')
      }
    } catch (brazeError) {
      console.warn('Cannot addAlias for Braze integration:', brazeError)
    }
  }

  /**
   * Link user to Heap analytics platform
   * @param customerId - Salesforce customer ID
   */
  const linkToHeap = (customerId: string): void => {
    try {
      // Using global heap object defined in integrationScripts.ts
      if (window?.heap?.identify) {
        window.heap.identify(customerId)
      }
    } catch (heapError) {
      console.warn('Cannot identify user for Heap.io integration:', heapError)
    }
  }

  /**
   * Links a Salesforce Customer ID to multiple analytics platforms
   * @returns Cleanup function to stop watching
   */
  const linkCustomerId = (): (() => void) => {
    return watch(
      () => [userStore.user?.customerId, userStore.guestUser?.customerId],
      async ([customerId, guestCustomerId]) => {
        try {
          const stitchingCustomerId = customerId || guestCustomerId
          if (!stitchingCustomerId) return

          // Link given customer ID as the 'Stitching ID' to our core digital analytics platforms
          linkToBraze(stitchingCustomerId)
          linkToHeap(stitchingCustomerId)
        } catch (error) {
          console.warn('Customer linking error for analytics integration:', error)
        }
      },
      { immediate: true },
    )
  }

  return {
    linkCustomerId,
  }
}
